import { AlarmClockIcon, BookOpenTextIcon, CalculatorIcon, FileQuestionIcon, KeyRoundIcon, LayersIcon, PencilRulerIcon, PresentationIcon, SearchXIcon, UsersIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

// MATEMATYKA GOTOWA™ copy (Polish), built on the WF GOTOWY™ landing model.
// Product mockup (WebP, transparent): box with the 10 volumes, hosted in Impultienda Recursos.
export const PRODUCT_IMAGE = 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/da84cebf-651c-4338-a4e9-6716f0b6e9ee/1792686363738_matematyka-gotowa-mockup-pudelko-10-tomow-42fcede3.webp';
export const PRICE = '79 zł';
export const OLD_PRICE = '349 zł';
export const PRICE_VALUE = 79;
export const heroGuarantees = ['Natychmiastowy dostęp do pobrania', 'Jednorazowa płatność', 'Bez subskrypcji', '7 dni gwarancji satysfakcji'];
export const stats = [{
  value: '10',
  label: 'Tomów z matematyki'
}, {
  value: '600+',
  label: 'Stron gotowych materiałów'
}, {
  value: '4→M',
  label: 'Od klasy 4 do matury'
}, {
  value: 'A/B',
  label: 'Wersje testów z kluczem odpowiedzi'
}, {
  value: '5',
  label: 'Bonusów w zestawie'
}];
export const differenceStats = [{
  value: 'Klasa 4 – matura',
  label: 'Jeden system od szkoły podstawowej po liceum'
}, {
  value: '10 cyfrowych tomów',
  label: 'Od liczb naturalnych po rachunek różniczkowy'
}, {
  value: 'PDF-y do wydruku',
  label: 'Format A4, na ekranie lub w wersji papierowej'
}];
export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Niedziele znikają na układaniu zadań',
  text: 'Godzinami przygotowujesz zestawy zadań na kolejny tydzień – a i tak nie wszystko jest gotowe.'
}, {
  title: 'Materiały z sieci są nieczytelne lub błędne',
  text: 'Zadania z różnych stron trzeba poprawiać, przepisywać i sprawdzać, zanim nadadzą się na lekcję.'
}, {
  title: 'Ręczne rysowanie brył i wykresów',
  text: 'Tracisz czas na rysowanie figur, osi liczbowych i wykresów funkcji, zamiast skupić się na uczniach.'
}, {
  title: 'Różne poziomy w jednej klasie',
  text: 'Jedni uczą się szybko, inni potrzebują więcej czasu – trudno przygotować zadania dla wszystkich.'
}, {
  title: 'Stres przed egzaminami',
  text: 'Egzamin ósmoklasisty i matura zbliżają się, a powtórkę trzeba przygotować w ostatniej chwili.'
}, {
  title: 'Brak kluczy odpowiedzi pod ręką',
  text: 'Sprawdzanie kartkówek i testów bez gotowego klucza to kolejne godziny pracy po lekcjach.'
}];
export const afterItems = ['Kompletny plan na każdy dział matematyki', 'Gotowe arkusze A4 z miejscem na obliczenia', 'Czyste diagramy, bryły i wykresy gotowe do użycia', 'Jasna progresja od prostych przykładów po zadania egzaminacyjne', 'Wskazówki dla nauczyciela i najczęstsze błędy uczniów', 'Kartkówki i testy w wersjach A i B', 'Klucze odpowiedzi do każdego testu', 'Plakaty dydaktyczne do sali', 'Materiały gotowe do wydruku'];
export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: AlarmClockIcon,
  title: 'Niedziele spędzasz na przygotowaniach',
  text: 'Czy poświęcasz część weekendu na układanie zadań i kart pracy na poniedziałek?'
}, {
  icon: SearchXIcon,
  title: 'Szukasz zadań na dziesięciu stronach',
  text: 'Czy ciągle szukasz zadań, które są przejrzyste, poprawne i motywują uczniów?'
}, {
  icon: UsersIcon,
  title: 'W klasie są bardzo różne poziomy',
  text: 'Czy trudno Ci prowadzić jedną lekcję dla uczniów, którzy liczą w zupełnie innym tempie?'
}, {
  icon: FileQuestionIcon,
  title: 'Egzamin ósmoklasisty i matura tuż-tuż',
  text: 'Czy musisz szybko przygotować powtórkę i arkusze egzaminacyjne?'
}, {
  icon: PencilRulerIcon,
  title: 'Sprawdziany i kartkówki zajmują czas',
  text: 'Czy chcesz mieć pod ręką gotowe testy w dwóch wersjach i klucze odpowiedzi?'
}];

// Spine colours mirror the box mockup (TOM 1 → TOM 10).
export const volumes: Array<{
  number: string;
  level: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Tom 01',
  level: 'Klasa 4',
  title: 'Liczby i działania',
  tagline: 'Solidne podstawy na start drugiego etapu edukacji.',
  intro: 'Pierwszy tom porządkuje liczby naturalne i działania – od rachunku pamięciowego po zadania tekstowe z życia codziennego.',
  items: ['Liczby naturalne i oś liczbowa', 'Działania pisemne', 'Kolejność wykonywania działań', 'Zadania tekstowe', 'Karty pracy do wydruku', 'Kartkówki z kluczem odpowiedzi'],
  outro: 'Otwierasz, wybierasz temat i wchodzisz do klasy z gotowymi zadaniami.',
  spine: 'bg-yellow-400 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Tom 02',
  level: 'Klasa 5',
  title: 'Ułamki i procenty',
  tagline: 'Ułamki, które uczniowie wreszcie widzą.',
  intro: 'Drugi tom prowadzi przez ułamki zwykłe i dziesiętne z modelami wizualnymi, krok po kroku.',
  items: ['Ułamki zwykłe', 'Skracanie i rozszerzanie', 'Ułamki dziesiętne', 'Wprowadzenie do procentów', 'Modele wizualne', 'Testy A i B'],
  outro: 'Ćwiczenia o rosnącej trudności, gotowe do rozdania.',
  spine: 'bg-green-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Tom 03',
  level: 'Klasa 6',
  title: 'Wyrażenia i równania',
  tagline: 'Pierwsze kroki w algebrze bez stresu.',
  intro: 'Trzeci tom wprowadza liczby ujemne, wyrażenia algebraiczne i proste równania.',
  items: ['Liczby ujemne', 'Wyrażenia algebraiczne', 'Proste równania', 'Zadania tekstowe', 'Karty pracy', 'Klucze odpowiedzi'],
  outro: 'Jasna progresja od przykładu do samodzielnego rozwiązania.',
  spine: 'bg-sky-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Tom 04',
  level: 'Klasa 7',
  title: 'Geometria płaska',
  tagline: 'Figury, pola i obwody narysowane profesjonalnie.',
  intro: 'Czwarty tom zawiera geometrię płaską z czystymi rysunkami, gotowymi do pracy na lekcji.',
  items: ['Trójkąty i czworokąty', 'Pola i obwody', 'Kąty', 'Twierdzenie Pitagorasa', 'Rysunki do wydruku', 'Testy A i B'],
  outro: 'Koniec z bazgraniem na tablicy.',
  spine: 'bg-purple-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Tom 05',
  level: 'Klasa 8',
  title: 'Geometria przestrzenna',
  tagline: 'Bryły, które da się zobaczyć.',
  intro: 'Piąty tom to bryły, ich siatki, pola powierzchni i objętości – oraz powtórka przed egzaminem ósmoklasisty.',
  items: ['Graniastosłupy i ostrosłupy', 'Siatki brył', 'Pole powierzchni', 'Objętość', 'Powtórka do egzaminu', 'Klucze odpowiedzi'],
  outro: 'Uczniowie widzą bryły, zamiast się ich bać.',
  spine: 'bg-blue-900 text-white',
  rule: 'border-white/30'
}, {
  number: 'Tom 06',
  level: 'Liceum 1',
  title: 'Funkcje i wykresy',
  tagline: 'Funkcje czytelne od pierwszej lekcji.',
  intro: 'Szósty tom otwiera liceum: funkcje, ich własności i wykresy, z gotowymi grafikami.',
  items: ['Pojęcie funkcji', 'Funkcja liniowa', 'Funkcja kwadratowa', 'Odczytywanie wykresów', 'Karty pracy', 'Testy A i B'],
  outro: 'Gotowe wykresy zamiast rysowania na tablicy.',
  spine: 'bg-cyan-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Tom 07',
  level: 'Liceum 2',
  title: 'Trygonometria',
  tagline: 'Sinus i cosinus krok po kroku.',
  intro: 'Siódmy tom zawiera trygonometrię z ilustracjami i zadaniami o rosnącej trudności.',
  items: ['Funkcje trygonometryczne', 'Trójkąt prostokątny', 'Tożsamości', 'Zadania z zastosowaniem', 'Karty pracy', 'Klucze odpowiedzi'],
  outro: 'Jasny plan na jeden z najtrudniejszych działów.',
  spine: 'bg-blue-700 text-white',
  rule: 'border-white/30'
}, {
  number: 'Tom 08',
  level: 'Liceum 3',
  title: 'Rachunek różniczkowy',
  tagline: 'Pochodne bez chaosu.',
  intro: 'Ósmy tom prowadzi przez granice i pochodne, z przykładami i zadaniami krok po kroku.',
  items: ['Granice', 'Pochodna funkcji', 'Monotoniczność i ekstrema', 'Zadania optymalizacyjne', 'Karty pracy', 'Testy A i B'],
  outro: 'Uporządkowany materiał na poziom rozszerzony.',
  spine: 'bg-emerald-600 text-white',
  rule: 'border-white/30'
}, {
  number: 'Tom 09',
  level: 'Matura',
  title: 'Matura – poziom podstawowy',
  tagline: 'Powtórka do matury bez szukania arkuszy.',
  intro: 'Dziewiąty tom to przygotowanie do matury podstawowej: arkusze, zadania i rozwiązania.',
  items: ['Arkusze ćwiczeniowe', 'Zadania zamknięte i otwarte', 'Rozwiązania krok po kroku', 'Powtórka działów', 'Klucze odpowiedzi'],
  outro: 'Spokojna powtórka z jasnym planem.',
  spine: 'bg-orange-500 text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Tom 10',
  level: 'Matura',
  title: 'Matura – poziom rozszerzony',
  tagline: 'Dla uczniów, którzy celują wyżej.',
  intro: 'Dziesiąty tom to zadania na poziom rozszerzony: analiza, geometria i zadania otwarte.',
  items: ['Analiza', 'Geometria', 'Zadania otwarte', 'Arkusze ćwiczeniowe', 'Rozwiązania krok po kroku'],
  outro: 'Materiał, który prowadzi aż do matury.',
  spine: 'bg-violet-600 text-white',
  rule: 'border-white/30'
}];
export const volumesIntro = {
  title: '10 cyfrowych tomów od klasy 4 do matury',
  text: 'Kompletny system materiałów z matematyki, który pomaga planować i prowadzić lekcje przez cały rok szkolny – w szkole podstawowej i w liceum.'
};
export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: LayersIcon,
  title: '10 kompletnych tomów',
  text: 'Od liczb naturalnych po geometrię przestrzenną, funkcje i maturę.'
}, {
  icon: CalculatorIcon,
  title: 'Ponad 600 stron',
  text: 'Uporządkowane tematycznie, w gotowym do druku formacie PDF A4.'
}, {
  icon: PencilRulerIcon,
  title: 'Karty pracy krok po kroku',
  text: 'Gotowe do rozdania na lekcji, z miejscem na obliczenia.'
}, {
  icon: BookOpenTextIcon,
  title: 'Rysunki i wykresy',
  text: 'Profesjonalne grafiki matematyczne: bryły, funkcje i diagramy.'
}, {
  icon: KeyRoundIcon,
  title: 'Testy i kartkówki',
  text: 'Wersje A i B z pełnym kluczem odpowiedzi.'
}, {
  icon: PresentationIcon,
  title: 'Plakaty dydaktyczne',
  text: 'Pomoce wizualne gotowe do powieszenia w sali.'
}];
export const pageSamples: Array<{
  src: string;
  label: string;
  title: string;
  text: string;
}> = [{
  src: 'https://cdn.magicpatterns.com/uploads/keqaaaTg4vozZndFvQLJNT/Captura-de-pantalla-2026-09-03-a-la_s_-11.13.21_p._m..webp',
  label: 'Klasa 4 · Zadania tekstowe',
  title: 'Zadania tekstowe – dzielenie',
  text: 'Zadania z życia codziennego z ilustracjami i oznaczeniem poziomu trudności w gwiazdkach.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/jBjBDboZiANYELsF63bcwY/Captura-de-pantalla-2026-09-03-a-la_s_-11.14.26_p._m..webp',
  label: 'Klasa 5 · Ułamki',
  title: 'Ćwiczenia – skracanie i rozszerzanie',
  text: 'Trzy bloki ćwiczeń z modelami wizualnymi ułamków i wskazówką dla nauczyciela.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/bHsNYhyrYL1ta8v4gZ8f4r/Captura-de-pantalla-2026-09-03-a-la_s_-11.15.26_p._m..webp',
  label: 'Klasa 6 · Liczby ujemne',
  title: 'Dodawanie i odejmowanie',
  text: 'Oś liczbowa krok po kroku i cztery zestawy ćwiczeń o rosnącej trudności.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/oRMXLoYzid8G9oSvPSSfyB/Captura-de-pantalla-2026-09-03-a-la_s_-11.16.16_p._m..webp',
  label: 'Plakat dydaktyczny',
  title: 'Tablica pierwiastków kwadratowych',
  text: 'Gotowy do druku plakat A3 z wizualnym modelem pól kwadratów od 1 do 100.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/595x6Nw6A5wuaUwz4G1Fvg/Captura-de-pantalla-2026-09-03-a-la_s_-11.17.13_p._m..webp',
  label: 'Klasa 8 · Geometria',
  title: 'Ćwiczenia – twierdzenie Pitagorasa',
  text: 'Od sprawdzania twierdzenia po rzeczywiste problemy i obliczanie pól figur.'
}];
export const pageTypes = ['Karty pracy', 'Zadania tekstowe', 'Plakaty dydaktyczne', 'Testy z kluczem odpowiedzi'];
export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'Baza kartkówek i testów',
  text: '55-stronicowy ebook PDF z gotowymi kartkówkami i testami do druku.',
  items: ['Gotowe kartkówki', 'Testy do druku', 'Klucze odpowiedzi'],
  benefit: 'Sprawdzian gotowy w kilka minut.'
}, {
  title: 'Atlas formuł i plakaty',
  text: '25 stron plakatów edukacyjnych A3/A4 gotowych do powieszenia w sali.',
  items: ['Wzory w jednym miejscu', 'Plakaty A3/A4', 'Do wydruku'],
  benefit: 'Sala, która uczy nawet na przerwie.'
}, {
  title: 'Komunikacja z rodzicami',
  text: '30-stronicowy poradnik z gotowymi szablonami e-maili i wiadomości.',
  items: ['Gotowe szablony e-maili', 'Wiadomości do rodziców'],
  benefit: 'Mniej czasu na pisanie do rodziców.'
}, {
  title: 'Planner Matematyka 2026/2027',
  text: '40-stronicowy planer nauczyciela do druku lub użytku cyfrowego.',
  items: ['Plan roku szkolnego', 'Do druku lub na ekranie'],
  benefit: 'Cały rok pod kontrolą.'
}, {
  title: 'Biblioteka gier i zabaw',
  text: '50 pomysłów na angażujące lekcje matematyki w 35-stronicowym przewodniku.',
  items: ['50 pomysłów na lekcje', 'Gry matematyczne', 'Do użycia od razu'],
  benefit: 'Lekcje, na które uczniowie czekają.'
}];
export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Otwórz MATEMATYKĘ GOTOWĄ™',
  text: 'Pobierz pakiet i otwórz tom dla swojej klasy.'
}, {
  title: 'Wybierz temat i materiały',
  text: 'Wybierz kartę pracy, zadania lub test dopasowany do działu, który realizujesz.'
}, {
  title: 'Wydrukuj',
  text: 'Wydrukuj to, czego potrzebujesz, albo korzystaj z materiałów na ekranie.'
}, {
  title: 'Prowadź lekcję',
  text: 'Wejdź do klasy z jasnym planem.'
}];
export const audienceItems = ['Uczysz matematyki w klasach 4–8', 'Uczysz w liceum lub przygotowujesz do matury', 'Udzielasz korepetycji z matematyki', 'Nie chcesz co tydzień układać zadań od zera', 'Pracujesz z uczniami o różnych poziomach', 'Chcesz mieć pod ręką testy i klucze odpowiedzi', 'Chcesz zaoszczędzić czas na przygotowaniach', 'Chcesz wchodzić na każdą lekcję z gotowym planem'];
export const timeValueTasks = ['Szukanie zadań na różnych stronach', 'Układanie kart pracy', 'Rysowanie brył i wykresów', 'Przygotowywanie powtórek do egzaminów', 'Testy, kartkówki i klucze odpowiedzi'];
export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '10 cyfrowych tomów',
  detail: 'Od liczb i ułamków po geometrię, funkcje, trygonometrię i maturę'
}, {
  label: '5 bonusów bez dodatkowych kosztów',
  detail: 'Kartkówki i testy, atlas formuł, komunikacja z rodzicami, planner i gry'
}, {
  label: 'Od klasy 4 do matury',
  detail: 'Ponad 600 stron: karty pracy, zadania, testy A/B i klucze odpowiedzi'
}];
export const offerIncludes = ['Natychmiastowy dostęp do pobrania', 'Pliki PDF do zapisania', 'Materiały do wydruku', 'Nie wysyłamy produktu fizycznego'];
export const faqs = [{
  q: 'Jak otrzymam materiały?',
  a: 'MATEMATYKA GOTOWA™ jest produktem cyfrowym. Po zakupie otrzymujesz dostęp do plików PDF, które możesz pobrać i zapisać. Możesz korzystać z nich na komputerze, tablecie lub telefonie i drukować to, czego potrzebujesz.'
}, {
  q: 'Czy pakiet jest przygotowany z myślą o polskiej szkole?',
  a: 'Tak. Materiały zostały przygotowane dla nauczycieli matematyki w polskich szkołach – od klasy 4 do matury. To niezależny materiał dydaktyczny, a nie oficjalny produkt Ministerstwa Edukacji Narodowej (MEN).'
}, {
  q: 'Dla których klas jest pakiet?',
  a: 'Od klasy 4 szkoły podstawowej, przez liceum, aż po maturę na poziomie podstawowym i rozszerzonym. Wybierasz tom odpowiedni do klasy i działu, który realizujesz.'
}, {
  q: 'Czy naprawdę mogę przygotować lekcję w 5 minut?',
  a: 'MATEMATYKA GOTOWA™ została stworzona po to, by przyspieszyć przygotowanie lekcji. Otwierasz tom, wybierasz kartę pracy lub zestaw zadań i zapoznajesz się z nim w około pięć minut. Czas może się różnić w zależności od tematu, klasy i potrzebnych dostosowań.'
}, {
  q: 'Czy są klucze odpowiedzi?',
  a: 'Tak. Testy i kartkówki mają wersje A i B z pełnym kluczem odpowiedzi.'
}, {
  q: 'Czy mogę drukować materiały?',
  a: 'Tak, wszystkie pliki są przygotowane do druku w formacie A4. Możesz też korzystać z nich na ekranie.'
}, {
  q: 'Czy pięć bonusów jest w cenie?',
  a: `Tak. Wszystkich pięć bonusów jest w pakiecie bez dodatkowych kosztów – całość za ${PRICE}.`
}, {
  q: 'Czy to subskrypcja?',
  a: 'Nie. Płacisz jednorazowo i otrzymujesz dostęp do materiałów cyfrowych. Bez żadnych opłat cyklicznych.'
}, {
  q: 'Czy mogę korzystać z materiałów na telefonie lub tablecie?',
  a: 'Tak. Pliki PDF otworzysz na komputerze, tablecie i telefonie. Aby sprawdzić układ stron przed wydrukiem, polecamy komputer lub tablet.'
}, {
  q: 'Czy jest gwarancja?',
  a: 'Tak. MATEMATYKA GOTOWA™ objęta jest 7-dniową gwarancją satysfakcji. Jeśli materiały nie spełnią Twoich oczekiwań, możesz skontaktować się z nami i poprosić o zwrot pieniędzy w okresie gwarancji, zgodnie z jej warunkami.'
}];
